import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Users2, ScrollText, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isStaffUser, rolesLabel } from "@/lib/constants";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);

  useEffect(() => { api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {}); }, []);

  const staff = isStaffUser(user);
  const active = courses.filter((c) => !c.expired);

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-5xl">
      <div className="flex items-center gap-2 text-primary mb-2">
        <Sparkles size={18} />
        <span className="text-sm font-medium">{rolesLabel(user)}</span>
      </div>
      <h1 className="text-4xl font-serif mb-2">Willkommen</h1>
      <p className="text-muted-foreground mb-10">{user?.email}</p>

      {staff && (
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-white border border-border rounded-lg p-5 shadow-sm">
            <GraduationCap className="text-primary mb-2" />
            <p className="text-2xl font-serif">{active.length}</p>
            <p className="text-sm text-muted-foreground">Aktive Kurse</p>
          </div>
          <div className="bg-white border border-border rounded-lg p-5 shadow-sm">
            <ScrollText className="text-primary mb-2" />
            <p className="text-2xl font-serif">{courses.length}</p>
            <p className="text-sm text-muted-foreground">Kurse gesamt</p>
          </div>
          <div className="bg-white border border-border rounded-lg p-5 shadow-sm">
            <Users2 className="text-primary mb-2" />
            <p className="text-lg font-serif">{rolesLabel(user)}</p>
            <p className="text-sm text-muted-foreground">Ihre Rolle(n)</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Ihre Kurse</h2>
        {staff && (
          <button
            onClick={() => navigate("/kurse?neu=1")}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-[#155E8D] transition-colors duration-200"
            data-testid="add-course-button"
          >
            <GraduationCap size={16} /> Neuer Kurs
          </button>
        )}
      </div>
      {courses.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {staff ? "Noch keine Kurse angelegt. Legen Sie unter „Kurse“ einen neuen Kurs an." : "Sie sind noch keinem Kurs zugeordnet."}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/kurs/${c.id}`)}
              className="text-left bg-white border border-border rounded-lg p-5 shadow-sm hover:border-primary transition-colors duration-200"
              data-testid={`dashboard-course-${c.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap size={18} className="text-primary" />
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{c.group}</span>
                {c.expired && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">offline</span>}
              </div>
              <p className="font-medium truncate">{c.kurs_name}</p>
              <p className="text-sm text-muted-foreground truncate">{c.kurs_gemeinde}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
